import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Modal from 'ext-ui/components/Modal/Modal'
import Processing from 'ext-ui/components/Processing/Processing'
import RadioGroup from 'ext-ui/components/RadioGroup/RadioGroup'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import { AsYouType } from 'libphonenumber-js'
import _ from 'lodash'
import moment from 'moment-timezone'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import { useTranslate } from 'src/localization/Trns'
import {
  KybBusinessInput,
  LegalStructure,
} from 'src/queries/payments/payments.types'
import {
  KybApiFieldError,
  KybValidationError,
} from 'src/queries/payments/paymentsKybError'

import { formatPartyAddress } from './bankAccountOnboarding'
import { KybAddressFieldset } from './KybAddressFieldset'
import { KybDocumentField } from './KybDocumentField'
import { kybInvalidMessage } from './kybErrorMessages'
import { kybModalStyle } from './kybModal.style'
import { KybOwnerCard } from './KybOwnerCard'
import {
  mapServerMessages,
  serverFieldSegments,
  stepForServerErrors,
} from './kybServerErrors'
import {
  BeneficialOwnerErrors,
  beneficialOwnersShown,
  buildKybRequest,
  digitsOnly,
  EMPTY_BENEFICIAL_OWNER,
  EMPTY_KYB_FORM_VALUES,
  isBusinessLegalStructure,
  isKybFormValid,
  KybFieldError,
  KybFormErrors,
  KybFormValues,
  KybOwnershipChoice,
  validateKybForm,
} from './paymentsKyb.validation'
import { CardDetailList, CardDetailRow } from './SettingsStatusCard'

export interface KybVerificationModalProps {
  open: boolean
  onClose: () => void
  /** Submit the assembled KYB request. Resolves on success (the connection
   * refetches to KYB_PENDING); rejects to surface the error state. */
  onSubmit: (request: KybBusinessInput) => Promise<unknown>
}

type ResultState = 'loading' | 'success' | 'error' | null

const LEGAL_STRUCTURE_VALUES: { value: LegalStructure; labelKey: string }[] = [
  { value: 'llc', labelKey: 'LLC' },
  { value: 'corporation', labelKey: 'Corporation' },
  { value: 'non_profit', labelKey: 'Non-profit' },
  { value: 'partnership', labelKey: 'Partnership' },
  { value: 'sole_proprietorship', labelKey: 'Sole Proprietorship' },
  { value: 'trust', labelKey: 'Trust' },
]

/** Cap on beneficial owners. Every beneficial owner must hold 25%+, so a
 * fifth could never fit under the 100% aggregate — a control person claiming
 * a stake counts toward this. */
const MAX_BENEFICIAL_OWNERS = 4

/** A fresh form. Index 0 is the control person — always present, never
 * removed, and not a beneficial owner until they say so. The default
 * ownership answer asserts a 25%+ owner exists, so one card is seeded to name
 * them; the block on advancing is that card's own empty required fields
 * rather than a separate "add an owner" rule. */
function makeInitialValues(): KybFormValues {
  return {
    ...EMPTY_KYB_FORM_VALUES,
    beneficialOwners: [
      { ...EMPTY_BENEFICIAL_OWNER, isBeneficialOwner: false },
      { ...EMPTY_BENEFICIAL_OWNER },
    ],
  }
}

/** Heading for one person on the confirm step. Roles derive from the slot,
 * not stored state: index 0 is the control person (who may additionally hold
 * a stake); everyone after is a numbered beneficial owner, matching the step-2
 * card titles. */
function ownerSummaryHeading(
  translate: (source: string, expressions?: Record<string, string>) => string,
  owner: KybFormValues['beneficialOwners'][number],
  index: number,
): string {
  if (index > 0) {
    return translate('Beneficial owner ${number}', { number: String(index) })
  }
  return owner.isBeneficialOwner
    ? `${translate('Control person')}, ${translate('Beneficial owner')}`
    : translate('Control person')
}

/** The confirm step shows only the last four — the full SSN never renders
 * outside its masked input. */
function maskedSsn(ssn: string): string {
  return ssn ? `•••-••-${ssn.slice(-4)}` : '—'
}

function formatKybDate(isoDate: string): string {
  return isoDate ? moment(isoDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '—'
}

/** Human label for a server error's field, so the banner names what to fix. */
function serverErrorLabel(
  translate: (source: string) => string,
  field: string | null,
): string | undefined {
  if (!field) return undefined
  const segments = serverFieldSegments(field)
  const leaf = segments[segments.length - 1]

  if (segments[0] === 'associations') {
    // associations[0] is the control person's card; the list cards after it
    // are titled "Beneficial owner N" starting at 1.
    const index = Number(segments[1])
    const ownerLabel = Number.isNaN(index)
      ? translate('Beneficial owner')
      : index === 0
        ? translate('Control person')
        : `${translate('Beneficial owner')} ${index}`
    const leafLabel = OWNER_FIELD_LABELS[leaf]
    return leafLabel ? `${ownerLabel} — ${translate(leafLabel)}` : ownerLabel
  }

  const rootLabel = BUSINESS_FIELD_LABELS[segments[0]]
  if (!rootLabel) return undefined
  const addressLeaf = segments[0] === 'businessAddress' && ADDRESS_LABELS[leaf]
  return addressLeaf
    ? `${translate(rootLabel)} — ${translate(addressLeaf)}`
    : translate(rootLabel)
}

const BUSINESS_FIELD_LABELS: { [root: string]: string } = {
  legalBusinessName: 'Legal Business Name',
  legalStructure: 'Legal Structure',
  dateFormed: 'Date of Incorporation',
  businessDescription: 'What Does Your Business Do?',
  intendedUse: "How You'll Use This Account",
  expectedActivityVolume: 'Expected Monthly Volume (USD)',
  usEin: 'Employer Identification Number (EIN)',
  email: 'Business Email',
  website: 'Website',
  phoneNumbers: 'Business Phone',
  businessAddress: 'Physical Business Address',
  documents: 'EIN Confirmation Letter',
}

const OWNER_FIELD_LABELS: { [leaf: string]: string } = {
  firstName: 'First name',
  lastName: 'Last name',
  dateOfBirth: 'Date of birth',
  email: 'Email',
  ssn: 'Social Security Number (SSN)',
  phoneNumber: 'Phone',
  relationshipTypes: 'Role',
  ownershipPercentage: 'Ownership % (25–100)',
  line1: 'Home address',
  line2: 'Home address',
  locality: 'Home address',
  region: 'Home address',
  postalCode: 'Home address',
  country: 'Home address',
}

const ADDRESS_LABELS: { [leaf: string]: string } = {
  line1: 'Address Line 1',
  line2: 'Address Line 2',
  locality: 'City',
  region: 'State',
  postalCode: 'ZIP Code',
  country: 'Country',
}

/** Step-1 fields in visual order, each with the `testId` its control renders
 * under. Drives "scroll to the first thing that is wrong" after a failed
 * advance — order here must track the rendered order. */
const STEP1_FIELD_TARGETS: { key: keyof KybFormErrors; testId: string }[] = [
  { key: 'legalBusinessName', testId: 'kyb-legal-name' },
  { key: 'legalStructure', testId: 'kyb-legal-structure' },
  { key: 'usEin', testId: 'kyb-ein' },
  { key: 'dateFormed', testId: 'kyb-date-formed' },
  { key: 'einLetterDocument', testId: 'kyb-ein-letter' },
  // The fieldset itself carries no test id — target its first field.
  { key: 'businessAddress', testId: 'kyb-physical-address-line1' },
  { key: 'registeredAddress', testId: 'kyb-registered-address-line1' },
  { key: 'businessDescription', testId: 'kyb-business-description' },
  { key: 'intendedUse', testId: 'kyb-intended-use' },
  { key: 'expectedActivityVolume', testId: 'kyb-volume' },
  { key: 'phoneNumber', testId: 'kyb-phone' },
  { key: 'email', testId: 'kyb-email' },
]

/** Owner fields in the order KybOwnerCard renders them, with the testId suffix
 * each control carries. Same purpose as STEP1_FIELD_TARGETS, per owner. */
const OWNER_FIELD_TARGETS: {
  key: keyof BeneficialOwnerErrors
  suffix: string
}[] = [
  { key: 'firstName', suffix: 'first-name' },
  { key: 'lastName', suffix: 'last-name' },
  { key: 'dateOfBirth', suffix: 'dob' },
  { key: 'email', suffix: 'email' },
  { key: 'phoneNumber', suffix: 'phone' },
  { key: 'ssn', suffix: 'ssn' },
  { key: 'ownershipPercentage', suffix: 'ownership' },
  { key: 'address', suffix: 'address-line1' },
]

const STEP1_KEYS: (keyof KybFormErrors)[] = [
  'legalBusinessName',
  'legalStructure',
  'dateFormed',
  'businessDescription',
  'intendedUse',
  'expectedActivityVolume',
  'usEin',
  'email',
  'website',
  'phoneNumber',
  'businessAddress',
  'registeredAddress',
  'einLetterDocument',
]

/**
 * "Verify your business" — the multi-step KYB modal. Three steps (business +
 * EIN details → beneficial owners → confirm & submit) over the ported KYB form
 * model + validation (`paymentsKyb.validation`), submitting via
 * `useSubmitLegalEntity` (passed in as `onSubmit`). Closing mid-flow with a
 * dirty form prompts a confirm-on-exit dialog ("You'll lose all progress");
 * a clean form closes silently. Rebuilt from the prototype's `KybModal` on
 * ext-ui primitives (`BaseModal`, `Stepper`, `Modal` for the exit guard).
 */
export function KybVerificationModal({
  open,
  onClose,
  onSubmit,
}: KybVerificationModalProps): ReactElement {
  const translate = useTranslate()

  const [values, setValues] = useState<KybFormValues>(makeInitialValues)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [result, setResult] = useState<ResultState>(null)
  const [dirty, setDirty] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [certify, setCertify] = useState(false)
  const [authorize, setAuthorize] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  // Field-level errors returned by a server 422 (`POST /payments/connection`),
  // surfaced on the form instead of the generic "something went wrong" card.
  const [serverErrors, setServerErrors] = useState<KybApiFieldError[] | null>(
    null,
  )
  // Steps the user has tried to advance past. Validation is computed every
  // render (it gates the flow), but a step's errors only become *visible* once
  // it is in here — otherwise a half-typed email reads as broken.
  const [attemptedSteps, setAttemptedSteps] = useState<readonly number[]>([])
  // A field to reveal once its step has rendered — submitting from step 3 can
  // send the user back to step 1, whose fields do not exist yet at click time.
  const [pendingReveal, setPendingReveal] = useState<string | null>(null)
  // Fields edited since the last advance attempt. Their errors stay hidden
  // until the next attempt re-checks them — otherwise a field being actively
  // typed into is judged mid-keystroke (a phone number is invalid until the
  // last digit lands).
  const [editedSinceAttempt, setEditedSinceAttempt] = useState<
    readonly string[]
  >([])

  // Guards a synchronous double-activation of submit (a second POST fired before
  // the first resolves); paired with disabling the control while loading.
  const submitInFlightRef = useRef(false)
  // The locked scrolling region + the step heading, for step-change scroll reset
  // and focus/announcement (a11y).
  const scrollRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const prevStepRef = useRef(step)
  // The KYB dialog body — marked inert while the exit-confirm dialog is up so the
  // two stacked modals don't both sit in the a11y tree with competing focus.
  const kybBodyRef = useRef<HTMLDivElement>(null)

  // Render-time reset when the dialog is opened or closed — the repo's convention
  // for "reset state when an external value changes" (avoids the
  // react-hooks/set-state-in-effect lint a useEffect version would trip).
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setValues(makeInitialValues())
      setStep(1)
      setResult(null)
      setDirty(false)
      setShowExitConfirm(false)
      setCertify(false)
      setAuthorize(false)
      setAgreeTerms(false)
      setServerErrors(null)
      setAttemptedSteps([])
      setPendingReveal(null)
      setEditedSinceAttempt([])
    } else {
      // Wipe sensitive PII (EIN, SSNs, DOBs, addresses, base64 documents) the
      // instant the dialog closes — otherwise it lingers in this still-mounted
      // component after a discard or success. `result`/`step` reset on the next
      // open, so they're left here to avoid flipping the closing dialog's
      // success/loading card back to the form mid fade-out.
      setValues(makeInitialValues())
      setDirty(false)
      setCertify(false)
      setAuthorize(false)
      setAgreeTerms(false)
      setServerErrors(null)
      setAttemptedSteps([])
      setPendingReveal(null)
      setEditedSinceAttempt([])
    }
  }

  const noteEdited = (keys: string[]): void =>
    setEditedSinceAttempt((prev) => [
      ...prev,
      ...keys.filter((key) => !prev.includes(key)),
    ])

  const update = (partial: Partial<KybFormValues>): void => {
    setDirty(true)
    noteEdited(Object.keys(partial))
    // A stale server error no longer applies once the user edits the form.
    setServerErrors(null)
    setValues((prev) => ({ ...prev, ...partial }))
  }
  const updateOwner = (
    index: number,
    partial: Partial<KybFormValues['beneficialOwners'][number]>,
  ): void => {
    setDirty(true)
    noteEdited(Object.keys(partial).map((key) => `owner:${index}:${key}`))
    setServerErrors(null)
    setValues((prev) => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.map((owner, i) =>
        i === index ? { ...owner, ...partial } : owner,
      ),
    }))
  }
  const addOwner = (): void => {
    setDirty(true)
    setServerErrors(null)
    setValues((prev) => ({
      ...prev,
      beneficialOwners: [
        ...prev.beneficialOwners,
        { ...EMPTY_BENEFICIAL_OWNER },
      ],
    }))
  }
  const removeOwner = (index: number): void => {
    setDirty(true)
    setServerErrors(null)
    // Edit-suppression keys are index-addressed; renumber them with the cards
    // or every card after the removed one inherits its neighbour's set and
    // hides errors that still block the step.
    setEditedSinceAttempt((prev) =>
      prev.flatMap((key) => {
        const match = key.match(/^owner:(\d+):(.*)$/)
        if (!match) return [key]
        const i = Number(match[1])
        if (i === index) return []
        return i > index ? [`owner:${i - 1}:${match[2]}`] : [key]
      }),
    )
    setValues((prev) => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.filter((_, i) => i !== index),
    }))
  }
  // The ownership question and the control person's own stake claim answer
  // each other: "nobody owns 25%+" includes the control person, and a control
  // person claiming a stake IS somebody owning 25%+. Whichever the user
  // touched last wins, so the two can never contradict.
  const chooseOwnership = (ownershipChoice: KybOwnershipChoice): void => {
    setDirty(true)
    noteEdited(['ownershipChoice'])
    setServerErrors(null)
    setValues((prev) => {
      if (ownershipChoice === 'noOwners') {
        return {
          ...prev,
          ownershipChoice,
          beneficialOwners: prev.beneficialOwners.map((owner, i) =>
            i === 0
              ? { ...owner, isBeneficialOwner: false, ownershipPercentage: '' }
              : owner,
          ),
        }
      }
      // Asserting a 25%+ owner exists seeds a card to name them — in the same
      // update as the answer, so the list never paints empty for a frame.
      const needsSeed =
        !prev.beneficialOwners[0]?.isBeneficialOwner &&
        prev.beneficialOwners.length === 1
      return {
        ...prev,
        ownershipChoice,
        beneficialOwners: needsSeed
          ? [...prev.beneficialOwners, { ...EMPTY_BENEFICIAL_OWNER }]
          : prev.beneficialOwners,
      }
    })
  }
  const updateControlPerson = (
    partial: Partial<KybFormValues['beneficialOwners'][number]>,
  ): void => {
    updateOwner(0, partial)
    if (partial.isBeneficialOwner === undefined) return
    if (partial.isBeneficialOwner) {
      update({ ownershipChoice: 'hasOwners' })
      // The seeded card existed only to name a 25%+ owner the control person
      // now is; still-untouched, it would demand a person who doesn't exist.
      // A card with anything typed in it stays (and becomes removable).
      setValues((prev) =>
        prev.beneficialOwners.length === 2 &&
        _.isEqual(prev.beneficialOwners[1], EMPTY_BENEFICIAL_OWNER)
          ? { ...prev, beneficialOwners: prev.beneficialOwners.slice(0, 1) }
          : prev,
      )
    } else {
      // Withdrawing the claim while "has owners" stands re-seeds the card
      // that names the 25%+ owner the answer still asserts.
      setValues((prev) =>
        prev.ownershipChoice === 'hasOwners' &&
        prev.beneficialOwners.length === 1
          ? {
              ...prev,
              beneficialOwners: [
                ...prev.beneficialOwners,
                { ...EMPTY_BENEFICIAL_OWNER },
              ],
            }
          : prev,
      )
    }
  }

  // On step change: return the scroll region to the top and move focus to the
  // new step heading so it is announced (the prior step's scroll offset and
  // focus must not bleed into the next step). Skipped on first render so we do
  // not steal focus from react-modal when the dialog opens.
  useEffect(() => {
    if (prevStepRef.current === step) return
    prevStepRef.current = step
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    headingRef.current?.focus()
  }, [step])

  useEffect(() => {
    if (!pendingReveal) return
    revealField(pendingReveal)
    setPendingReveal(null)
  }, [pendingReveal, step])

  // Mark the KYB dialog inert while the exit-confirm dialog is open — inert moves
  // focus out and hides it from the a11y tree, so the two stacked modals don't
  // fight over focus.
  useEffect(() => {
    const node = kybBodyRef.current
    if (!node) return
    if (showExitConfirm) {
      node.setAttribute('inert', '')
      node.setAttribute('aria-hidden', 'true')
    } else {
      node.removeAttribute('inert')
      node.removeAttribute('aria-hidden')
    }
  }, [showExitConfirm])

  const errors = validateKybForm(values)
  const serverMessages = mapServerMessages(serverErrors)
  const showErrors = attemptedSteps.includes(step)
  const ownersListShown = beneficialOwnersShown(values)
  // "Has owners" with nobody to name it cannot arise at render time: every
  // transition that could produce it seeds a card in its own state update
  // (makeInitialValues, chooseOwnership, updateControlPerson).
  const controlIsOwner = values.beneficialOwners[0]?.isBeneficialOwner ?? false
  // A control person holding a stake occupies one of the four beneficial-owner
  // seats, leaving one fewer card the list may add.
  const maxListedOwners = MAX_BENEFICIAL_OWNERS - (controlIsOwner ? 1 : 0)

  // Prefer a local format-error message; fall back to a server field message.
  // Server messages ignore `showErrors` — a 422 IS the submit attempt.
  const errorVisible = (key: string): boolean =>
    showErrors && !editedSinceAttempt.includes(key)
  const fieldError = (
    key: keyof KybFormValues,
    code: KybFieldError | undefined,
  ): string | undefined =>
    (errorVisible(key) ? kybInvalidMessage(translate, code) : undefined) ??
    serverMessages.fields[key]

  /** Owner errors minus whatever the user has edited since the last attempt. */
  const visibleOwnerErrors = (
    index: number,
  ): BeneficialOwnerErrors | undefined => {
    if (!showErrors) return undefined
    const ownerErrors = errors.beneficialOwners?.[index]
    if (!ownerErrors) return undefined
    const visible = Object.fromEntries(
      Object.entries(ownerErrors).filter(
        ([key]) => !editedSinceAttempt.includes(`owner:${index}:${key}`),
      ),
    )
    return visible as BeneficialOwnerErrors
  }
  const step1Valid = STEP1_KEYS.every((key) => errors[key] === undefined)
  const step2Valid =
    values.beneficialOwners.length > 0 &&
    errors.beneficialOwners === undefined &&
    errors.ownershipTotal === undefined
  const allAcknowledged = certify && authorize && agreeTerms

  // Scroll the locked body to a field and put the caret in it. jsdom has no
  // scrollIntoView, and a focus() that scrolls would fight the smooth scroll.
  const revealField = (testId: string): void => {
    const root = scrollRef.current
    if (!root) return
    const group =
      root.querySelector(`[data-testid="${testId}-group"]`) ??
      root.querySelector(`[data-testid="${testId}"]`) ??
      root.querySelector(`[id="${testId}"]`)
    const input = root.querySelector<HTMLElement>(
      `[data-testid="${testId}-input"]`,
    )
    input?.focus({ preventScroll: true })
    const target = group ?? input
    if (target instanceof HTMLElement && target.scrollIntoView) {
      target.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')
          ?.matches
          ? 'auto'
          : 'smooth',
        block: 'center',
      })
    }
  }

  /** First step-1 field carrying an error, in rendered order. */
  const firstStep1ErrorTarget = (): string | undefined =>
    STEP1_FIELD_TARGETS.find(({ key }) => errors[key] !== undefined)?.testId

  /** First owner field carrying an error, in card order then field order. */
  const firstStep2ErrorTarget = (): string | undefined => {
    const ownerErrors = errors.beneficialOwners ?? []
    for (let index = 0; index < ownerErrors.length; index += 1) {
      const target = OWNER_FIELD_TARGETS.find(
        ({ key }) => ownerErrors[index]?.[key] !== undefined,
      )
      if (target) return `kyb-owner-${index}-${target.suffix}`
    }
    return undefined
  }

  const markAttempted = (attempted: number): void => {
    // A fresh attempt re-judges everything, including fields edited since the
    // last one.
    setEditedSinceAttempt([])
    setAttemptedSteps((prev) =>
      prev.includes(attempted) ? prev : [...prev, attempted],
    )
  }

  /** Advance if the step is clean; otherwise reveal what is wrong. */
  const advanceFrom = (from: 1 | 2, valid: boolean, next: 2 | 3): void => {
    markAttempted(from)
    if (valid) {
      setStep(next)
      return
    }
    if (from === 1) {
      const target = firstStep1ErrorTarget()
      if (target) revealField(target)
      return
    }
    // Owner errors sit far down a long card — scrolling to the top instead
    // showed a filled-looking form with the actual problem off-screen.
    const target = firstStep2ErrorTarget()
    if (target) {
      revealField(target)
    } else {
      // Only the step-level notes (ownership total, control-person count) are
      // wrong; those render below the cards — and only after this attempt's
      // re-render, so the scroll must wait for it.
      setPendingReveal('kyb-ownership-note')
    }
  }

  /** Submit, or send the user to the step that is actually blocking it. */
  const attemptSubmit = (): void => {
    markAttempted(3)
    if (!isKybFormValid(errors)) {
      const step1Target = firstStep1ErrorTarget()
      if (step1Target) {
        markAttempted(1)
        setStep(1)
        setPendingReveal(step1Target)
        return
      }
      markAttempted(2)
      setStep(2)
      return
    }
    if (!allAcknowledged) {
      // The gate note renders at the bottom of the scroll region only after
      // this attempt's re-render — reveal it or the block is invisible.
      setPendingReveal('kyb-ack-note')
      return
    }
    void handleSubmit()
  }

  const doClose = (): void => {
    setShowExitConfirm(false)
    onClose()
  }
  const requestClose = (): void => {
    if (result === 'loading') return // non-dismissible in flight
    if (result === 'success') {
      doClose()
      return
    }
    if (dirty) {
      setShowExitConfirm(true)
      return
    }
    doClose()
  }

  const handleSubmit = async (): Promise<void> => {
    // Synchronous guard: a double-activation must not fire a second KYB POST
    // before the first resolves (backend idempotency is out of scope).
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    setResult('loading')
    setServerErrors(null)
    try {
      await onSubmit(buildKybRequest(values))
      setResult('success')
    } catch (error) {
      // A 422 carries field-level errors the user can fix — surface them on the
      // form (banner + inline) and drop back onto the step that owns the failing
      // field (nested owner/DOB/address errors land on step 2, not step 1),
      // instead of the generic dead-end error card that discarded them.
      if (error instanceof KybValidationError && error.fieldErrors.length > 0) {
        setServerErrors(error.fieldErrors)
        setResult(null)
        setStep(stepForServerErrors(error.fieldErrors))
        return
      }
      setResult('error')
    } finally {
      submitInFlightRef.current = false
    }
  }

  const selectedStructure = LEGAL_STRUCTURE_VALUES.find(
    (o) => o.value === values.legalStructure,
  )

  const title =
    result === 'loading'
      ? translate('Verifying your business')
      : result === 'success'
        ? translate('Submitted for verification')
        : result === 'error'
          ? translate('Something went wrong')
          : step === 1
            ? translate('Verify your business')
            : step === 2
              ? translate('Add owners')
              : translate('Confirm details')

  const subtitle =
    result || step === 3
      ? undefined
      : step === 1
        ? translate(
            'One-time verification required before you can add a bank account.',
          )
        : translate(
            'U.S. financial regulations require us to verify anyone who owns 25% or more of this business, or who controls its finances.',
          )

  const acknowledgements: {
    key: string
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }[] = [
    {
      key: 'certify',
      label: translate(
        'I certify that the information provided is true, accurate, and complete to the best of my knowledge.',
      ),
      checked: certify,
      onChange: setCertify,
    },
    {
      key: 'authorize',
      label: translate(
        "I authorize Extend and its banking partner, Modern Treasury, to verify this business's identity and information using the details provided above.",
      ),
      checked: authorize,
      onChange: setAuthorize,
    },
    {
      key: 'terms',
      label: translate(
        "I acknowledge and agree to Extend's Terms of Service and Modern Treasury's Platform Agreement, and consent to this information being processed for verification purposes.",
      ),
      checked: agreeTerms,
      onChange: setAgreeTerms,
    },
  ]

  const renderFooter = (): ReactElement => {
    // In flight: no navigation out of the submit, so only the pending action shows.
    if (result === 'loading') {
      return (
        <div className="KybModal-footer single">
          <Button variant="primary" loading testId="kyb-submit">
            {translate('Submit for Verification')}
          </Button>
        </div>
      )
    }
    if (result === 'success') {
      return (
        <div className="KybModal-footer single">
          <Button variant="primary" onClick={doClose} testId="kyb-done">
            {translate('Continue')}
          </Button>
        </div>
      )
    }
    if (result === 'error') {
      return (
        <div className="KybModal-footer">
          <Button variant="no-style" onClick={doClose} testId="kyb-close">
            {translate('Close')}
          </Button>
          <Button
            variant="primary"
            onClick={(): void => setResult(null)}
            testId="kyb-retry"
          >
            {translate('Try again')}
          </Button>
        </div>
      )
    }
    if (step === 1) {
      return (
        <div className="KybModal-footer">
          <Button variant="no-style" onClick={requestClose} testId="kyb-cancel">
            {translate('Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={(): void => advanceFrom(1, step1Valid, 2)}
            testId="kyb-next"
          >
            {translate('Next')}
          </Button>
        </div>
      )
    }
    if (step === 2) {
      return (
        <div className="KybModal-footer">
          <Button
            variant="secondary"
            onClick={(): void => setStep(1)}
            testId="kyb-back"
          >
            {translate('Back')}
          </Button>
          <Button
            variant="primary"
            onClick={(): void => advanceFrom(2, step2Valid, 3)}
            testId="kyb-next"
          >
            {translate('Next')}
          </Button>
        </div>
      )
    }
    return (
      <div className="KybModal-footer">
        <Button
          variant="secondary"
          onClick={(): void => setStep(2)}
          testId="kyb-back"
        >
          {translate('Back')}
        </Button>
        <Button variant="primary" onClick={attemptSubmit} testId="kyb-submit">
          {translate('Submit for Verification')}
        </Button>
      </div>
    )
  }

  return (
    <>
      <BaseModal
        isOpen={open}
        onClose={requestClose}
        variant="popup"
        showCloseButton={result !== 'loading'}
        shouldCloseOnEsc={result !== 'loading'}
        shouldCloseOnOverlayClick={result !== 'loading'}
        aria-label={title}
      >
        <div className="KybModal" css={kybModalStyle} ref={kybBodyRef}>
          <div className="KybModal-header">
            <h2 className="KybModal-title" tabIndex={-1} ref={headingRef}>
              {title}
            </h2>
            {subtitle && <p className="KybModal-subtitle">{subtitle}</p>}
            {serverErrors && !result && (
              <AlertPanel variant="severe">
                <div
                  className="KybModal-serverErrors"
                  data-testid="kyb-server-errors"
                  role="alert"
                >
                  <span>
                    {translate('Please correct the following and resubmit:')}
                  </span>
                  <ul>
                    {serverErrors.map((err, index) => {
                      const label = serverErrorLabel(translate, err.field)
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                          {label ? `${label}: ${err.message}` : err.message}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </AlertPanel>
            )}
          </div>

          <div
            className="KybModal-scroll"
            ref={scrollRef}
            role="group"
            aria-label={title}
          >
            {result === 'loading' && (
              <div className="KybModal-result" role="status" aria-live="polite">
                <Processing width={32} height={32} />
                <p className="KybModal-resultText">
                  {translate(
                    "Submitting your business's details to Modern Treasury.",
                  )}
                </p>
              </div>
            )}

            {result === 'success' && (
              <div className="KybModal-result">
                <CheckmarkCircleIcon width={48} height={48} />
                <p className="KybModal-resultTitle">
                  {translate('Business verification submitted')}
                </p>
                <p className="KybModal-resultText">
                  {translate(
                    "We're reviewing your details — usually a few minutes, up to 48 hours. We'll let you know the moment it's done.",
                  )}
                </p>
              </div>
            )}

            {result === 'error' && (
              <div className="KybModal-result">
                <AlertTriangleIcon width={40} height={40} />
                <p className="KybModal-resultText">
                  {translate(
                    'We could not submit your business details for verification. Nothing you entered was lost — try again.',
                  )}
                </p>
              </div>
            )}

            {!result && step === 1 && (
              <div className="KybModal-content step1">
                <AlertPanel
                  variant="default"
                  className="KybModal-infoPanel"
                  data-testid="kyb-documents-info"
                >
                  <div className="KybModal-infoBox">
                    <span className="KybModal-infoBoxTitle">
                      {translate('Before you start, have these ready')}
                    </span>
                    <ul className="KybModal-infoBoxList">
                      <li>
                        {translate(
                          'Your EIN and a copy of your EIN confirmation letter',
                        )}
                      </li>
                      <li>
                        {translate(
                          'Control person and beneficial owner details',
                        )}
                      </li>
                    </ul>
                  </div>
                </AlertPanel>

                {/* Section 1 — Business Identity (DBA field descoped: needs an
                    extend-api contract change — see project note). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('Business Identity')}
                  </h3>
                  <TextInput
                    label={translate('Legal Business Name')}
                    requirement="required"
                    placeholder={translate('e.g. Acme Manufacturing LLC')}
                    value={values.legalBusinessName}
                    onChange={(e): void =>
                      update({ legalBusinessName: e.target.value })
                    }
                    errorText={fieldError(
                      'legalBusinessName',
                      errors.legalBusinessName,
                    )}
                    testId="kyb-legal-name"
                  />
                  <SingleDropdown
                    data-testid="kyb-legal-structure"
                    label={translate('Legal Structure')}
                    requirement="required"
                    placeholder={translate('Select a structure')}
                    options={LEGAL_STRUCTURE_VALUES.map((o) => ({
                      value: o.value,
                      label: translate(o.labelKey),
                    }))}
                    selectedValue={
                      selectedStructure
                        ? {
                            value: selectedStructure.value,
                            label: translate(selectedStructure.labelKey),
                          }
                        : undefined
                    }
                    onSelect={(v): void =>
                      update({ legalStructure: v.value as LegalStructure })
                    }
                    clearable={false}
                    errorText={fieldError(
                      'legalStructure',
                      errors.legalStructure,
                    )}
                  />
                </div>

                {/* Section 2 — Tax & Registration (EIN → Date → EIN letter). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('Tax & Registration')}
                    <TooltipIcon>
                      {translate(
                        'We verify your business against IRS records, so this must match your EIN letter exactly.',
                      )}
                    </TooltipIcon>
                  </h3>
                  <TextInput
                    label={translate('Employer Identification Number (EIN)')}
                    requirement="required"
                    placeholder={translate('e.g. 123456789')}
                    value={values.usEin}
                    maxLength={9}
                    onChange={(e): void =>
                      update({ usEin: digitsOnly(e.target.value).slice(0, 9) })
                    }
                    errorText={fieldError('usEin', errors.usEin)}
                    testId="kyb-ein"
                  />
                  <SingleDateInputField
                    id="kyb-date-formed"
                    required
                    fieldLabel={
                      <>
                        {translate('Date of Incorporation')}
                        <span
                          className="KybModal-requiredMark"
                          aria-hidden="true"
                        >
                          {' *'}
                        </span>
                      </>
                    }
                    allowPastAndCurrentDates
                    dateMoment={
                      values.dateFormed
                        ? moment(values.dateFormed, 'YYYY-MM-DD')
                        : undefined
                    }
                    onDateMomentChange={(m): void =>
                      update({ dateFormed: m ? m.format('YYYY-MM-DD') : '' })
                    }
                    errors={
                      fieldError('dateFormed', errors.dateFormed)
                        ? [
                            fieldError(
                              'dateFormed',
                              errors.dateFormed,
                            ) as string,
                          ]
                        : undefined
                    }
                  />
                  <KybDocumentField
                    label={translate('EIN Confirmation Letter')}
                    requirement={
                      isBusinessLegalStructure(values.legalStructure)
                        ? 'required'
                        : 'optional'
                    }
                    tooltip={translate(
                      'Upload the IRS letter (CP-575 or 147C). Must be dated within the last 6 months.',
                    )}
                    errorText={fieldError(
                      'einLetterDocument',
                      errors.einLetterDocument,
                    )}
                    microcopy={translate(
                      'A recent IRS EIN confirmation letter or proof of address.',
                    )}
                    value={values.einLetterDocument}
                    onChange={(doc): void => update({ einLetterDocument: doc })}
                    testId="kyb-ein-letter"
                  />
                </div>

                {/* Section 3 — Physical business address. */}
                <KybAddressFieldset
                  legend={translate('Physical Business Address')}
                  idPrefix="kyb-physical-address"
                  value={values.businessAddress}
                  onChange={(businessAddress): void =>
                    update({ businessAddress })
                  }
                  errors={
                    errorVisible('businessAddress')
                      ? errors.businessAddress
                      : undefined
                  }
                  serverMessages={serverMessages.businessAddress}
                />

                {/* Section 4 — Registered address (optional to enable). */}
                <label className="KybModal-checkboxRow">
                  <Checkbox
                    aria-label={translate('Same as physical business address')}
                    checked={!values.includeRegisteredAddress}
                    onChange={(e): void =>
                      update({ includeRegisteredAddress: !e.target.checked })
                    }
                    data-testid="kyb-same-address"
                  />
                  <span>{translate('Same as physical business address')}</span>
                </label>
                {values.includeRegisteredAddress && (
                  <KybAddressFieldset
                    legend={translate('Registered Address')}
                    idPrefix="kyb-registered-address"
                    value={values.registeredAddress}
                    onChange={(registeredAddress): void =>
                      update({ registeredAddress })
                    }
                    errors={
                      errorVisible('registeredAddress')
                        ? errors.registeredAddress
                        : undefined
                    }
                    serverMessages={serverMessages.registeredAddress}
                  />
                )}

                {/* Section 5 — More About Your Business (merges the old
                    description / purpose / volume + business contact fields;
                    Website dropped to match the prototype). */}
                <div className="KybModal-section">
                  <h3 className="KybModal-sectionTitle">
                    {translate('More About Your Business')}
                  </h3>
                  <TextInput
                    label={translate('What Does Your Business Do?')}
                    requirement="required"
                    placeholder={translate(
                      'e.g. Custom metal fabrication for commercial clients',
                    )}
                    value={values.businessDescription}
                    onChange={(e): void =>
                      update({ businessDescription: e.target.value })
                    }
                    errorText={fieldError(
                      'businessDescription',
                      errors.businessDescription,
                    )}
                    testId="kyb-business-description"
                  />
                  <TextInput
                    label={translate("How You'll Use This Account")}
                    requirement="required"
                    placeholder={translate('e.g. Paying vendors and suppliers')}
                    value={values.intendedUse}
                    onChange={(e): void =>
                      update({ intendedUse: e.target.value })
                    }
                    errorText={fieldError('intendedUse', errors.intendedUse)}
                    testId="kyb-intended-use"
                  />
                  <TextInput
                    label={translate('Expected Monthly Volume (USD)')}
                    requirement="required"
                    placeholder={translate('e.g. 25000')}
                    iconLeft={<span aria-hidden="true">{'$'}</span>}
                    inputMode="numeric"
                    microcopy={translate(
                      'Your best estimate of total payments per month.',
                    )}
                    value={values.expectedActivityVolume}
                    // Not digits-only: stripping the input turned "1.50" into
                    // 150 and "-500" into 500, so a rejected value became a
                    // silently different accepted one. Validation already
                    // rejects anything but a positive whole amount.
                    onChange={(e): void =>
                      update({ expectedActivityVolume: e.target.value })
                    }
                    errorText={fieldError(
                      'expectedActivityVolume',
                      errors.expectedActivityVolume,
                    )}
                    testId="kyb-volume"
                  />
                  {/* Phone auto-formats as you type (libphonenumber AsYouType,
                      US) like the app's other phone inputs; validation and the
                      KYB request normalize the formatted value to E.164. */}
                  <TextInput
                    label={translate('Business Phone')}
                    requirement="required"
                    placeholder={translate('e.g. (212) 555-0123')}
                    value={values.phoneNumber}
                    onChange={(e): void =>
                      update({
                        phoneNumber: new AsYouType('US').input(e.target.value),
                      })
                    }
                    errorText={fieldError('phoneNumber', errors.phoneNumber)}
                    testId="kyb-phone"
                  />
                  <TextInput
                    label={translate('Business Email')}
                    requirement="required"
                    placeholder={translate('e.g. accounting@acme.com')}
                    value={values.email}
                    onChange={(e): void => update({ email: e.target.value })}
                    errorText={fieldError('email', errors.email)}
                    testId="kyb-email"
                  />
                </div>
              </div>
            )}

            {!result && step === 2 && (
              <div className="KybModal-content">
                {/* One fixed control person, then a list that only exists when
                    somebody actually holds 25%+. Roles come from the slot a
                    person occupies, so there is no role selection and no
                    cardinality to police. */}
                <KybOwnerCard
                  index={0}
                  owner={values.beneficialOwners[0]}
                  variant="control"
                  onUpdate={updateControlPerson}
                  errors={visibleOwnerErrors(0)}
                  serverMessages={serverMessages.owners[0]}
                />

                <RadioGroup<KybOwnershipChoice>
                  name="kyb-ownership-choice"
                  label={translate('Business Ownership')}
                  required
                  value={values.ownershipChoice}
                  onChange={chooseOwnership}
                  options={[
                    {
                      value: 'hasOwners',
                      title: translate('One or more people own 25% or more'),
                      testId: 'kyb-ownership-has-owners',
                    },
                    {
                      value: 'noOwners',
                      title: translate('No individual owns 25% or more'),
                      description: translate(
                        'e.g. a public company or a regulated financial institution',
                      ),
                      testId: 'kyb-ownership-no-owners',
                    },
                  ]}
                  testId="kyb-ownership-choice"
                />

                {ownersListShown && (
                  <>
                    <div className="KybModal-ownerList">
                      {values.beneficialOwners.slice(1).map((owner, offset) => (
                        <KybOwnerCard
                          // eslint-disable-next-line react/no-array-index-key
                          key={offset}
                          index={offset + 1}
                          owner={owner}
                          variant="beneficial"
                          onUpdate={(partial): void =>
                            updateOwner(offset + 1, partial)
                          }
                          onRemove={
                            // The last remaining card is the mandatory minimum
                            // while the control person holds no stake —
                            // removing it would just get re-added empty.
                            controlIsOwner ||
                            offset > 0 ||
                            values.beneficialOwners.length > 2
                              ? (): void => removeOwner(offset + 1)
                              : undefined
                          }
                          errors={visibleOwnerErrors(offset + 1)}
                          serverMessages={serverMessages.owners[offset + 1]}
                        />
                      ))}
                    </div>
                    <div>
                      {values.beneficialOwners.length - 1 < maxListedOwners ? (
                        <Button
                          variant="secondary"
                          onClick={addOwner}
                          testId="kyb-add-owner"
                        >
                          {translate('Add another beneficial owner')}
                        </Button>
                      ) : (
                        <p className="KybModal-gateNote">
                          {translate(
                            'Maximum of ${count} beneficial owners reached.',
                            { count: String(MAX_BENEFICIAL_OWNERS) },
                          )}
                        </p>
                      )}
                    </div>
                  </>
                )}
                {showErrors && errors.ownershipTotal && (
                  <p
                    className="KybModal-gateNote alert"
                    role="alert"
                    data-testid="kyb-ownership-note"
                  >
                    {translate(
                      'Total ownership across all owners cannot exceed 100%.',
                    )}
                  </p>
                )}
              </div>
            )}

            {!result && step === 3 && (
              <div className="KybModal-content">
                <div className="KybModal-summary">
                  <div className="KybModal-summaryHeader">
                    <h3 className="KybModal-summaryTitle">
                      {translate('Business details')}
                    </h3>
                    <Button
                      variant="text"
                      onClick={(): void => setStep(1)}
                      testId="kyb-edit-business"
                    >
                      {translate('Update')}
                    </Button>
                  </div>
                  <CardDetailList>
                    <CardDetailRow
                      label={translate('Legal Business Name')}
                      value={values.legalBusinessName || '—'}
                    />
                    <CardDetailRow
                      label={translate('Legal Structure')}
                      value={
                        selectedStructure
                          ? translate(selectedStructure.labelKey)
                          : '—'
                      }
                    />
                    <CardDetailRow
                      label={translate('Employer Identification Number (EIN)')}
                      value={values.usEin || '—'}
                    />
                    <CardDetailRow
                      label={translate('Physical Address')}
                      value={formatPartyAddress(values.businessAddress) || '—'}
                    />
                    <CardDetailRow
                      label={translate('Expected monthly volume')}
                      value={values.expectedActivityVolume || '—'}
                    />
                  </CardDetailList>
                </div>

                <div className="KybModal-summary">
                  <div className="KybModal-summaryHeader">
                    <h3 className="KybModal-summaryTitle">
                      {translate('Owners')}
                    </h3>
                    <Button
                      variant="text"
                      onClick={(): void => setStep(2)}
                      testId="kyb-edit-owners"
                    >
                      {translate('Update')}
                    </Button>
                  </div>
                  <div className="KybModal-ownerSummaryList">
                    {/* Only the people actually collected — the list is absent
                        when nobody holds 25%+. */}
                    {(ownersListShown
                      ? values.beneficialOwners
                      : values.beneficialOwners.slice(0, 1)
                    ).map((owner, index) => (
                      <div
                        className="KybModal-ownerSummary"
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        data-testid={`kyb-owner-summary-${index}`}
                      >
                        <h4 className="KybModal-ownerSummaryTitle">
                          {ownerSummaryHeading(translate, owner, index)}
                        </h4>
                        <CardDetailList>
                          <CardDetailRow
                            label={translate('Name')}
                            value={
                              `${owner.firstName} ${owner.lastName}`.trim() ||
                              translate('Unnamed')
                            }
                          />
                          <CardDetailRow
                            label={translate('Date of birth')}
                            value={formatKybDate(owner.dateOfBirth)}
                          />
                          <CardDetailRow
                            label={translate('Email')}
                            value={owner.email || '—'}
                          />
                          <CardDetailRow
                            label={translate('Phone')}
                            value={owner.phoneNumber || '—'}
                          />
                          <CardDetailRow
                            label={translate('SSN')}
                            value={maskedSsn(owner.ssn)}
                          />
                          {(index === 0 ? owner.isBeneficialOwner : true) && (
                            <CardDetailRow
                              label={translate('Ownership %')}
                              value={
                                owner.ownershipPercentage
                                  ? `${owner.ownershipPercentage}%`
                                  : '—'
                              }
                            />
                          )}
                          <CardDetailRow
                            label={translate('Home address')}
                            value={formatPartyAddress(owner.address) || '—'}
                          />
                        </CardDetailList>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="KybModal-ack">
                  {acknowledgements.map((item) => (
                    <label key={item.key} className="KybModal-ackRow">
                      <Checkbox
                        aria-label={item.label}
                        checked={item.checked}
                        isInvalid={showErrors && !item.checked}
                        onChange={(e): void => item.onChange(e.target.checked)}
                        data-testid={`kyb-ack-${item.key}`}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                  {showErrors && !allAcknowledged && (
                    <p
                      className="KybModal-gateNote alert"
                      role="alert"
                      data-testid="kyb-ack-note"
                    >
                      {translate('Confirm all three statements to submit.')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          {renderFooter()}
        </div>
      </BaseModal>

      <Modal
        open={showExitConfirm}
        onClose={(): void => setShowExitConfirm(false)}
        title={translate('Leave without saving?')}
        description={translate("You'll lose all progress.")}
        footer={
          <div className="KybModal-footer">
            <Button
              variant="secondary"
              onClick={(): void => setShowExitConfirm(false)}
              testId="kyb-exit-keep"
            >
              {translate('Keep editing')}
            </Button>
            <Button
              variant="severe"
              onClick={doClose}
              testId="kyb-exit-discard"
            >
              {translate('Leave')}
            </Button>
          </div>
        }
      />
    </>
  )
}
